import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Button,
  Tooltip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from '@mui/material';
import { 
  Payment, 
  Cancel, 
  CheckCircle, 
  Visibility, 
  WarningAmber,
  CreditCard
} from '@mui/icons-material';
import { formatDate } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/formatUtils';
import { penalidadService } from '../../services/penalidadService';

const PenalidadesList = ({ pacienteId }) => {
  const [penalidades, setPenalidades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagoDialogOpen, setPagoDialogOpen] = useState(false);
  const [anularDialogOpen, setAnularDialogOpen] = useState(false);
  const [selectedPenalidad, setSelectedPenalidad] = useState(null);
  const [formaPago, setFormaPago] = useState('Efectivo');
  const [observacionesPago, setObservacionesPago] = useState('');
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [procesingAction, setProcessingAction] = useState(false);

  const loadPenalidades = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await penalidadService.getPenalidadesByPaciente(pacienteId);
      setPenalidades(data);
    } catch (err) {
      console.error("Error al cargar penalidades:", err);
      setError("No se pudieron cargar las penalidades");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pacienteId) {
      loadPenalidades();
    }
  }, [pacienteId]);

  const handleOpenPagoDialog = (penalidad) => {
    setSelectedPenalidad(penalidad);
    setFormaPago('Efectivo');
    setObservacionesPago('');
    setPagoDialogOpen(true);
  };

  const handleOpenAnularDialog = (penalidad) => {
    setSelectedPenalidad(penalidad);
    setMotivoAnulacion('');
    setAnularDialogOpen(true);
  };

  const handleClosePagoDialog = () => {
    setPagoDialogOpen(false);
    setSelectedPenalidad(null);
  };

  const handleCloseAnularDialog = () => {
    setAnularDialogOpen(false);
    setSelectedPenalidad(null);
  };

  const handleRegistrarPago = async () => {
    if (!selectedPenalidad) return;

    setProcessingAction(true);
    try {
      await penalidadService.registrarPagoPenalidad(selectedPenalidad.id, {
        metodo_pago: formaPago,
        observaciones: observacionesPago,
        fecha_pago: new Date().toISOString().split('T')[0]
      });
      
      handleClosePagoDialog();
      await loadPenalidades();
    } catch (err) {
      console.error("Error al registrar pago:", err);
      setError("No se pudo registrar el pago");
    } finally {
      setProcessingAction(false);
    }
  };

  const handleAnularPenalidad = async () => {
    if (!selectedPenalidad || !motivoAnulacion) return;

    setProcessingAction(true);
    try {
      await penalidadService.anularPenalidad(selectedPenalidad.id, motivoAnulacion);
      
      handleCloseAnularDialog();
      await loadPenalidades();
    } catch (err) {
      console.error("Error al anular penalidad:", err);
      setError("No se pudo anular la penalidad");
    } finally {
      setProcessingAction(false);
    }
  };

  const renderEstadoChip = (estado) => {
    switch (estado) {
      case 'Pendiente':
        return <Chip size="small" icon={<WarningAmber />} label="Pendiente" color="warning" />;
      case 'Pagada':
        return <Chip size="small" icon={<CheckCircle />} label="Pagada" color="success" />;
      case 'Anulada':
        return <Chip size="small" icon={<Cancel />} label="Anulada" color="error" />;
      default:
        return <Chip size="small" label={estado} />;
    }
  };

  if (loading && penalidades.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Penalidades
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {penalidades.length === 0 ? (
        <Alert severity="info">
          No hay penalidades registradas para este paciente
        </Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Monto</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {penalidades.map((penalidad) => (
                <TableRow key={penalidad.id}>
                  <TableCell>{formatDate(penalidad.fecha_aplicacion)}</TableCell>
                  <TableCell>{penalidad.tipo}</TableCell>
                  <TableCell>{formatCurrency(penalidad.monto)}</TableCell>
                  <TableCell>{renderEstadoChip(penalidad.estado)}</TableCell>
                  <TableCell>
                    <Box display="flex">
                      <Tooltip title="Ver detalles">
                        <IconButton size="small">
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      
                      {penalidad.estado === 'Pendiente' && (
                        <>
                          <Tooltip title="Registrar pago">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleOpenPagoDialog(penalidad)}
                            >
                              <Payment fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Anular penalidad">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleOpenAnularDialog(penalidad)}
                            >
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Diálogo para registrar pago */}
      <Dialog 
        open={pagoDialogOpen} 
        onClose={handleClosePagoDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <CreditCard color="primary" sx={{ mr: 1 }} />
            Registrar Pago de Penalidad
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box my={2}>
            <Typography variant="subtitle2">
              Tipo: <strong>{selectedPenalidad?.tipo}</strong>
            </Typography>
            <Typography variant="subtitle2">
              Monto: <strong>{selectedPenalidad && formatCurrency(selectedPenalidad.monto)}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fecha: {selectedPenalidad && formatDate(selectedPenalidad.fecha_aplicacion)}
            </Typography>
          </Box>

          <TextField
            select
            fullWidth
            label="Forma de Pago"
            value={formaPago}
            onChange={(e) => setFormaPago(e.target.value)}
            margin="normal"
            SelectProps={{
              native: true,
            }}
          >
            <option value="Efectivo">Efectivo</option>
            <option value="Tarjeta">Tarjeta</option>
            <option value="Transferencia">Transferencia</option>
            <option value="Otro">Otro</option>
          </TextField>

          <TextField
            fullWidth
            label="Observaciones"
            value={observacionesPago}
            onChange={(e) => setObservacionesPago(e.target.value)}
            margin="normal"
            multiline
            rows={2}
            placeholder="Información adicional sobre el pago..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePagoDialog} color="inherit" disabled={procesingAction}>
            Cancelar
          </Button>
          <Button 
            onClick={handleRegistrarPago} 
            color="primary"
            variant="contained"
            disabled={procesingAction}
            startIcon={procesingAction ? <CircularProgress size={20} /> : null}
          >
            Registrar Pago
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo para anular penalidad */}
      <Dialog 
        open={anularDialogOpen} 
        onClose={handleCloseAnularDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <Cancel color="error" sx={{ mr: 1 }} />
            Anular Penalidad
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box my={2}>
            <Typography variant="subtitle2">
              Tipo: <strong>{selectedPenalidad?.tipo}</strong>
            </Typography>
            <Typography variant="subtitle2">
              Monto: <strong>{selectedPenalidad && formatCurrency(selectedPenalidad.monto)}</strong>
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Fecha: {selectedPenalidad && formatDate(selectedPenalidad.fecha_aplicacion)}
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Motivo de Anulación"
            value={motivoAnulacion}
            onChange={(e) => setMotivoAnulacion(e.target.value)}
            margin="normal"
            multiline
            rows={3}
            placeholder="Indique el motivo por el que se anula esta penalidad..."
            required
            error={!motivoAnulacion && anularDialogOpen}
            helperText={!motivoAnulacion && anularDialogOpen ? "El motivo es obligatorio" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAnularDialog} color="inherit" disabled={procesingAction}>
            Cancelar
          </Button>
          <Button 
            onClick={handleAnularPenalidad} 
            color="error"
            variant="contained"
            disabled={procesingAction || !motivoAnulacion}
            startIcon={procesingAction ? <CircularProgress size={20} /> : null}
          >
            Anular Penalidad
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PenalidadesList;